import type { TodaySchedule } from "@allergy123/contracts/clinical";
import { Button, Card, Status, Table, Td, Th } from "@allergy123/ui";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useClinical, useLoad } from "../api";
import { errorText } from "../lib/errors";
import { formatCalendarDate, formatTime, fullName, kindLabel, shiftDate, statusLabel, todayIn } from "../lib/format";
import { useSession } from "../session";

type Item = TodaySchedule["items"][number];

/** The location's schedule for one day: check in, start the encounter, or open the one in progress. */
export function TodayPage() {
  const s = useSession();
  const api = useClinical();
  const navigate = useNavigate();
  const timezone = s.location?.timezone ?? "America/Denver";
  const [date, setDate] = useState(() => todayIn(timezone));
  const data = useLoad((a) => (s.location ? a.dashboard.today({ locationId: s.location.locationId, date }) : Promise.resolve(null)), [s.location?.locationId, date]);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  async function checkIn(item: Item) {
    setBusy(item.appointment.appointmentId);
    setNote(null);
    try {
      await api.appointments.checkIn(item.appointment.appointmentId, { version: item.appointment.version });
      data.reload();
    } catch (err) {
      setNote(`Couldn't check in: ${errorText(err)}`);
    } finally {
      setBusy(null);
    }
  }

  async function start(item: Item) {
    setBusy(item.appointment.appointmentId);
    setNote(null);
    try {
      const detail = await api.encounters.create({ patientId: item.appointment.patientId, locationId: item.appointment.locationId, kind: item.appointment.kind, appointmentId: item.appointment.appointmentId });
      navigate(`/encounters/${detail.encounter.encounterId}`);
    } catch (err) {
      setNote(`Couldn't start the encounter: ${errorText(err)}`);
      setBusy(null);
    }
  }

  const items = data.data?.items ?? [];
  const isToday = date === todayIn(timezone);
  return (
    <>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="mb-2 text-h1 font-semibold text-anchor">{isToday ? "Today" : formatCalendarDate(date)}</h1>
          <p className="max-w-[68ch] text-ink-secondary">
            {s.location ? `${s.location.name} · ${formatCalendarDate(date)} · ${timezone}` : "Your membership covers no location at this practice."} Check patients in as they arrive, then start their encounter.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setDate(shiftDate(date, -1))}>
            Previous day
          </Button>
          <Button variant="secondary" onClick={() => setDate(todayIn(timezone))} disabled={isToday}>
            Today
          </Button>
          <Button variant="secondary" onClick={() => setDate(shiftDate(date, 1))}>
            Next day
          </Button>
        </div>
      </div>
      {data.state === "error" && (
        <div className="mb-6">
          <Status kind="error" label={`Couldn't load the schedule: ${errorText(data.error)}`} />
        </div>
      )}
      {note && (
        <div className="mb-6">
          <Status kind="error" label={note} />
        </div>
      )}
      <Card>
        <Table caption={data.state === "ready" ? `${items.length} appointment${items.length === 1 ? "" : "s"}` : "Loading…"}>
          <thead>
            <tr>
              <Th>Time</Th>
              <Th>Patient</Th>
              <Th>Visit</Th>
              <Th>Appointment</Th>
              <Th>Encounter</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {data.state === "ready" && items.length === 0 && (
              <tr>
                <Td colSpan={6} className="text-ink-secondary">
                  Nothing scheduled. Walk-ins start from the patient's page.
                </Td>
              </tr>
            )}
            {items.map((item) => {
              const a = item.appointment;
              const e = item.encounter;
              const working = busy === a.appointmentId;
              return (
                <tr key={a.appointmentId}>
                  <Td>{formatTime(a.scheduledAt, timezone)}</Td>
                  <Td>
                    <Link to={`/patients/${a.patientId}`}>{fullName(a.patient)}</Link>
                    <span className="block text-small text-ink-secondary">Born {a.patient.dateOfBirth}</span>
                  </Td>
                  <Td>
                    {kindLabel(a.kind)}
                    {a.walkIn && <span className="block text-small text-ink-secondary">Walk-in</span>}
                  </Td>
                  <Td>{statusLabel(a.status)}</Td>
                  <Td>{e ? statusLabel(e.status) : "—"}</Td>
                  <Td>
                    <div className="flex flex-wrap gap-2">
                      {a.status === "scheduled" && s.can("clinical:write") && (
                        <Button variant="secondary" disabled={working} onClick={() => void checkIn(item)}>
                          Check in
                        </Button>
                      )}
                      {!e && a.status !== "cancelled" && a.status !== "completed" && s.can("clinical:write") && (
                        <Button disabled={working} onClick={() => void start(item)}>
                          Start {kindLabel(a.kind).toLowerCase()}
                        </Button>
                      )}
                      {e && (
                        <Button variant="secondary" href={`/encounters/${e.encounterId}`}>
                          Open
                        </Button>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card>
    </>
  );
}
